import type { ApiClientError } from '@vrplatform/api';
import { isUUID } from '@vrplatform/utils';
import { isFailedToFetchError } from './api.retry';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const clerkOfflineErrorCode = /\(\s*code\s*=\s*["']clerk_offline["']\s*\)/i;
const clerkSessionUnavailableErrorMessages = new Set([
  'unable to authenticate the request, you need to supply an active session',
  'you are signed out',
]);

export const isClerkOfflineErrorMessage = (message: string): boolean =>
  clerkOfflineErrorCode.test(message);

export const isClerkSessionUnavailableErrorMessage = (
  message: string
): boolean => clerkSessionUnavailableErrorMessages.has(message.toLowerCase());

/**
 * The API maps every JWT verification failure — expiry, signature mismatch,
 * missing claims, revoked sessions — onto this exact response, and refuses it
 * in auth middleware before any route handler runs.
 *
 * That exact pair is the only rejection the API client replays, and therefore
 * the only one worth suppressing at the query layer: the central recovery
 * already reported it. Other `UNAUTHORIZED` messages stay reportable because
 * they can be actionable, and they must not be replayed — a route-level 401 may
 * already have run a side effect, and a fresh credential cannot change it.
 *
 * The same predicate gates both sides so the replay trigger and the reporting
 * suppression cannot drift apart.
 */
export const isInvalidJwtApiErrorBody = (body: unknown): boolean =>
  isRecord(body) &&
  body.code === 'UNAUTHORIZED' &&
  body.message === 'Invalid JWT';

export const isInvalidJwtApiError = (error: ApiClientError) =>
  isInvalidJwtApiErrorBody(error);

export const shouldCaptureApiQueryError = (
  error: ApiClientError,
  sentryQueryKey?: string
) => {
  const context: unknown = error.context;
  const isExpectedConnectionReconnect =
    error.code === 'CONNECTION_RECONNECT_REQUIRED' &&
    isRecord(context) &&
    context.action === 'reconnect' &&
    typeof context.connectionId === 'string' &&
    isUUID(context.connectionId);
  const isExpectedListingGroupValidation =
    error.code === 'FORBIDDEN' &&
    isRecord(context) &&
    (((context.reason === 'lockedAccountingData' ||
      context.reason === 'publishedOwnerStatements' ||
      context.reason === 'ownerStatementAttachedJournalEntries') &&
      (context.supportAction === 'listingGroupHistoryMigration' ||
        context.nextAction === 'mergeListingChildren')) ||
      (context.reason === 'inReviewOwnerStatements' &&
        context.nextAction === 'returnStatementsToDraft'));
  const isExpectedListingGroupDryRunValidation =
    error.code === 'BAD_REQUEST' &&
    sentryQueryKey === 'listing-group-parent-dry-run' &&
    ((isRecord(context) && context.type === 'ownershipMismatch') ||
      (isRecord(context) &&
        context.reason === 'nonZeroOpeningBalance' &&
        context.nextAction === 'clearListingOpeningBalance') ||
      error.message ===
        'Parent and child listings must have matching active ownership' ||
      error.message === 'Listing already belongs to a group' ||
      error.message === 'Parent listing already belongs to a group' ||
      error.reason === 'parentListingOwnershipMissing');
  const isExpectedListingDeleteValidation =
    error.code === 'BAD_REQUEST' &&
    sentryQueryKey === 'listing-delete-dry-run' &&
    isRecord(context) &&
    context.canArchive === true &&
    context.suggestedOnLocked === 'archive';
  const isExpectedPlaidHealthRateLimit =
    error.code === 'RATE_LIMITED' &&
    sentryQueryKey === 'plaid-health' &&
    isRecord(context) &&
    context.operation === 'plaid.health.item.get' &&
    context.plaidErrorCode === 'ITEM_GET_LIMIT';
  const isExpectedPlaidHealthRelink =
    error.code === 'PLAID_CONNECT_REQUIRES_NEW_LINK' &&
    sentryQueryKey === 'plaid-health' &&
    isRecord(context) &&
    context.action === 'replace' &&
    context.restartMode === 'replace' &&
    typeof context.connectionId === 'string' &&
    isUUID(context.connectionId);

  // PRO-18816: the operation poller treats transport failures as transient and
  // polls again on its own interval, so a failed poll is not terminal.
  const isExpectedOperationPollTransportFailure =
    sentryQueryKey === 'operation-detail' &&
    !error.code &&
    isFailedToFetchError(error);

  return (
    !isExpectedConnectionReconnect &&
    !isExpectedOperationPollTransportFailure &&
    !isExpectedListingGroupValidation &&
    !isExpectedListingGroupDryRunValidation &&
    !isExpectedListingDeleteValidation &&
    !isExpectedPlaidHealthRateLimit &&
    !isExpectedPlaidHealthRelink
  );
};

// helper to fetch all items from an api endpoint
export async function fetchApiAllItems<TItem>(
  fetchItems: (
    page: number
  ) => Promise<{ items: TItem[]; hasNextPage: boolean }>,
  page = 1,
  prevs: TItem[] = []
): Promise<TItem[]> {
  const { items, hasNextPage } = await fetchItems(page);

  const newItems = prevs.concat(items);

  if (!hasNextPage) return newItems;

  const nextPage = page + 1;
  return await fetchApiAllItems(fetchItems, nextPage, newItems);
}

/**
 * PRO-17318: every query error is captured from the same frame, so the default
 * stack grouping cannot tell two API errors apart. An API error envelope with a
 * code carries a static message, so the message separates its issues at bounded
 * cardinality. Code-less errors can carry arbitrary client or GraphQL text, so
 * their message stays out of the fingerprint.
 *
 * An error response with an empty body has no code or message of its own. It
 * signals an outage or proxy failure, not a query defect, so it groups by HTTP
 * status across all queries.
 */
export const getApiQueryErrorSentryMetadata = ({
  emptyResponseStatus,
  errorCode,
  message,
  queryGroup,
  sentryQueryKey,
}: {
  emptyResponseStatus?: number;
  errorCode?: string;
  message: string;
  queryGroup: string;
  sentryQueryKey?: string;
}) => {
  const normalizedErrorCode = errorCode || 'unknown-code';
  const normalizedQueryKey = sentryQueryKey || 'unclassified';

  return {
    fingerprint: errorCode
      ? [
          '{{ default }}',
          'api-query',
          errorCode,
          queryGroup,
          normalizedQueryKey,
          message,
        ]
      : emptyResponseStatus
        ? ['api-query-empty-response', String(emptyResponseStatus)]
        : [
            '{{ default }}',
            'api-query',
            normalizedErrorCode,
            queryGroup,
            normalizedQueryKey,
          ],
    tags: {
      api_error_code: normalizedErrorCode,
      api_query_group: queryGroup,
      api_query_key: normalizedQueryKey,
      error_source: 'api-query',
    },
  };
};

/**
 * Parses a regional API base URL that came from a response body or local
 * storage and refuses everything but VRPlatform API hosts: the control-plane
 * host's own domain over HTTPS, or `localhost` over HTTP when the control plane
 * is the local dev API. Authenticated requests are sent to the result.
 */
export const parseVrpApiBaseUrl = (
  apiBaseUrl: string,
  controlPlaneApiHost: string
): URL => {
  let url: URL;
  try {
    url = new URL(apiBaseUrl);
  } catch {
    throw new Error('Regional API base URL must be a valid URL');
  }

  const controlPlaneHostname = controlPlaneApiHost.split(':')[0];
  if (controlPlaneApiHost === 'localhost:8877') {
    if (url.protocol === 'http:' && url.hostname === 'localhost') return url;
    throw new Error('Regional API base URL must be a local API host');
  }

  const parentDomain = controlPlaneHostname.split('.').slice(-2).join('.');
  if (
    url.protocol === 'https:' &&
    (url.hostname === parentDomain || url.hostname.endsWith(`.${parentDomain}`))
  )
    return url;
  throw new Error('Regional API base URL must be a VRPlatform API host');
};
